/** Unit options for Total Facility Area & Build-Up Area — order matches design spec. */
export const FACILITY_AREA_UNIT_OPTIONS = [
  { value: 'mtr', label: 'mtr' },
  { value: 'acre', label: 'Acre' },
  { value: 'h_acre', label: 'H Acre' },
  { value: 'sq', label: 'Sq' },
  { value: 'sq_feet', label: 'Sq feet' },
  { value: 'metre', label: 'Metre' },
  { value: 'km', label: 'Km' },
];

export const FACILITY_AREA_UNIT_LABEL_MAP = Object.fromEntries(
  FACILITY_AREA_UNIT_OPTIONS.map((o) => [o.value, o.label]),
);

/** Map legacy / alternate stored values to canonical option values. */
export const FACILITY_AREA_UNIT_ALIASES = {
  sq_ft: 'sq_feet',
  sqft: 'sq_feet',
  sq_m: 'sq',
  sqm: 'sq',
  square_feet: 'sq_feet',
  square_meters: 'sq',
  square_meter: 'sq',
  acres: 'acre',
  hectare: 'h_acre',
  hectares: 'h_acre',
  hacre: 'h_acre',
  meter: 'metre',
  meters: 'metre',
  metres: 'metre',
  kilometer: 'km',
  kilometers: 'km',
  kilometre: 'km',
  kilometres: 'km',
};

export const resolveFacilityAreaUnit = (unit) => {
  if (unit == null || unit === '') return 'sq_feet';
  const raw = String(unit).trim();
  const token = raw.toLowerCase().replace(/\./g, '').replace(/\s+/g, '_');
  if (FACILITY_AREA_UNIT_LABEL_MAP[token]) return token;
  if (FACILITY_AREA_UNIT_ALIASES[token]) return FACILITY_AREA_UNIT_ALIASES[token];
  const byLabel = FACILITY_AREA_UNIT_OPTIONS.find(
    (o) => o.label.toLowerCase() === raw.toLowerCase(),
  );
  if (byLabel) return byLabel.value;
  return token;
};

export const facilityAreaUnitLabel = (unit) => {
  const resolved = resolveFacilityAreaUnit(unit);
  return FACILITY_AREA_UNIT_LABEL_MAP[resolved] || String(unit || 'Sq feet').replace(/_/g, ' ');
};

const matchUnitToken = (raw) => {
  const token = String(raw || '')
    .toLowerCase()
    .trim()
    .replace(/\./g, '')
    .replace(/\s+/g, '_');
  if (!token) return null;
  return resolveFacilityAreaUnit(token);
};

const normalizeAreaPair = (source, valueKey, unitKey, snakeValue, snakeUnit) => {
  let value = source[valueKey] ?? source[snakeValue];
  let unit = resolveFacilityAreaUnit(source[unitKey] ?? source[snakeUnit]);

  if (typeof value === 'string' && value.trim()) {
    const combined = value.trim().match(/^([\d.,]+)\s+(.+)$/);
    if (combined && source[unitKey] == null && source[snakeUnit] == null) {
      value = combined[1];
      unit = matchUnitToken(combined[2]) || unit;
    }
  }

  return {
    [valueKey]: value != null && value !== '' ? String(value) : '',
    [unitKey]: unit,
  };
};

/** Map snake_case + legacy combined values into camelCase number + unit fields. */
export const normalizeFacilityAreaFields = (fd) => {
  if (!fd || typeof fd !== 'object') return fd;
  const out = { ...fd };
  Object.assign(
    out,
    normalizeAreaPair(out, 'totalFacilityArea', 'totalFacilityAreaUnit', 'total_facility_area', 'total_facility_area_unit'),
  );
  Object.assign(
    out,
    normalizeAreaPair(out, 'buildUpArea', 'buildUpAreaUnit', 'build_up_area', 'build_up_area_unit'),
  );
  delete out.total_facility_area;
  delete out.total_facility_area_unit;
  delete out.build_up_area;
  delete out.build_up_area_unit;
  return out;
};

export const formatFacilityArea = (value, unit) => {
  if (value == null || value === '') return '';
  return `${value} ${facilityAreaUnitLabel(unit)}`.trim();
};

export const formatFacilityAreaPair = (fd, valueKey, unitKey, snakeValue, snakeUnit) =>
  formatFacilityArea(
    fd?.[valueKey] ?? fd?.[snakeValue],
    fd?.[unitKey] ?? fd?.[snakeUnit],
  );
